
import { Vue, Options } from "vue-class-component";
import OverlayPanel from "primevue/overlaypanel";
import { ellipsisString } from "@/app/infrastructures/misc/Utils";
import formatValueNumber from "@/app/infrastructures/misc/common-library/FormatValueNumber";
import router from "@/app/ui/router";
import { IncomingOutgoingController } from "@/app/ui/controllers/IncomingOutgoingController";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import { NGenController } from "@/app/ui/controllers/NGenController";
import { CargoV2Data } from "@/domain/entities/CargoV2";
import PrintStatusIcon from "@/app/ui/views/cargo-v2/components/print-status-icon.vue";
import Print from "@/app/ui/views/cargo-v2/modules/print.vue";
import { FlagsPermissionCargo } from "@/feature-flags/flags-permission-cargo";
import WidgetDownloadCargo from "@/app/ui/views/cargo/component/widget-download-cargo.vue";
import CalculateTimeDifference from "@/app/infrastructures/misc/common-library/CalculateTimeDifference";
import { DownloadAWBRequest } from "@/data/payload/api/CargoV2ApiRequest";
import { FlagsPermissionCargoV2 } from "@/feature-flags/flags-permission-cargo-v2";

interface PrintStatusItem {
  label: string;
  printed: boolean;
}

type CargoV2PrintFlag = "manifest" | "label" | "pti" | "sptj";

interface CargoV2ActionGroupOption {
  icon: string;
  label: string;
  clickFunction: (item: CargoV2Data) => void;
}

interface CargoV2PrintRef {
  printManifestV2?: (
    id: string | number,
    row?: CargoV2Data | null,
    onSuccess?: (flag: CargoV2PrintFlag) => void
  ) => void;
  printData?: (
    cargoNumber: string | number,
    type: string,
    name: string,
    row?: CargoV2Data | null,
    onSuccess?: (flag: CargoV2PrintFlag) => void
  ) => void;
  printPtiPdf?: (
    id: string | number,
    row?: CargoV2Data | null,
    onSuccess?: (flag: CargoV2PrintFlag) => void
  ) => void;
  printSPTJElektronik?: (
    cargoNumber: string | number,
    row?: CargoV2Data | null,
    onSuccess?: (flag: CargoV2PrintFlag) => void
  ) => void;
  printSptjNonElectronic?: (
    id: string | number,
    row?: CargoV2Data | null,
    onSuccess?: (flag: CargoV2PrintFlag) => void
  ) => void;
}

@Options({
  components: {
    OverlayPanel,
    PrintStatusIcon,
    Print,
    WidgetDownloadCargo
  }
})
class IncomingOutgoingCargoTable extends Vue {
  printPopoverItem: CargoV2Data | null = null;
  ellipsisString = ellipsisString;

  get permissionDownloadAwbReceiptSync() {
    return FlagsPermissionCargoV2.permission_cargo_v2_download_awb_receipt_sync_enable.isEnabled();
  }

  get cargoList(): CargoV2Data[] {
    return IncomingOutgoingController.cargoList.data;
  }

  get pagination() {
    return IncomingOutgoingController.cargoPagination;
  }

  set pagination(p: typeof IncomingOutgoingController.cargoPagination) {
    IncomingOutgoingController.setCargoPagination(p);
  }

  get isLoading(): boolean {
    return IncomingOutgoingController.cargoLoading;
  }

  get errorCause(): string {
    return IncomingOutgoingController.cargoErrorCause;
  }

  get cargoListAvailable(): boolean {
    return (
      !this.cargoList.length && !this.isLoading && this.pagination.page === 1
    );
  }

  get columns() {
    return [
      {
        name: "No. Kargo/SMU",
        styleHead: "w-80 text-left whitespace-no-wrap align-middle break-all",
        "custom-slot": true,
        slotName: "number_cargo"
      },
      {
        name: "Kota asal - tujuan",
        styleHead:
          "min-w-[12rem] w-40 text-left whitespace-nowrap align-middle",
        "custom-slot": true,
        slotName: "origin_destination"
      },
      {
        name: "ETD",
        styleHead: "w-40 text-left",
        "custom-slot": true,
        slotName: "etd"
      },
      {
        name: "Total koli/Gross w.",
        styleHead: "w-56 text-left whitespace-nowrap align-middle",
        render: (item: CargoV2Data) =>
          `<div class="overflow-ellipsis text-left">${
            item.totalPieces
          } koli / ${formatValueNumber(item.cargoTotalCustomGrossWeight)} kg</div>`
      },
      {
        name: "Status print",
        styleHead: "w-40 text-left whitespace-no-wrap align-middle",
        "custom-slot": true,
        slotName: "status_print"
      },
      {
        name: "Selisih berat",
        styleHead: "w-48 text-left align-middle",
        "custom-slot": true,
        slotName: "selisih_berat"
      },
      {
        name: "Terakhir diupdate",
        styleHead: "w-56 text-left whitespace-no-wrap align-middle",
        render: (item: CargoV2Data) => {
          const date = item.updatedAt
            ? this.formatDate(item.updatedAt, "DD MMMM YYYY, HH:mm")
            : "-";
          return item.updatedAt
            ? `<div class="flex flex-col text-sm text-gray-lp-300">
                  <div class="overflow-ellipsis text-left"> ${date} WIB </div>
                  <div class="truncate text-gray-lp-600 w-32">
                    ${item.updatedBy || "-"}
                  </div>
                  <div> ${item.cargoHubName || "-"} </div>
                </div>`
            : `<div class="overflow-ellipsis text-left"> - </div>`;
        }
      },
      {
        name: "Action",
        key: "actionable_column",
        styleHead: "w-28 text-left align-middle",
        showButton: () => true
      }
    ];
  }

  setPrintFlag(row: CargoV2Data, flag: CargoV2PrintFlag): void {
    if (flag === "manifest") row.isPrintManifest = true;
    else if (flag === "label") row.isPrintLabel = true;
    else if (flag === "pti") row.isPrintPti = true;
    else if (flag === "sptj") row.isPrintSptj = true;
  }

  getPrintStatusItems(item: CargoV2Data): PrintStatusItem[] {
    const isPlane = item.cargoType === "plane";
    const isBTH =
      item.originCityCode === "BTH" || item.destinationCityCode === "BTH";

    const label = { label: "Label kargo", printed: item.isPrintLabel };
    const awb = { label: "AWB Lion Cargo", printed: item.isPrintAwb };
    const pti = { label: "PTI", printed: item.isPrintPti };
    const sptj = { label: "SPTJ", printed: item.isPrintSptj };
    const manifest = { label: "Manifest kargo", printed: item.isPrintManifest };

    if (!isPlane) return [label, manifest];

    if (isBTH) return [label, awb, manifest];

    const items: PrintStatusItem[] = [label, awb];

    if (FlagsPermissionCargo.permission_cargo_print_pti_pdf.isEnabled()) {
      items.push(pti);
    }

    if (
      (FlagsPermissionCargo.permission_cargo_print_electronic_sptj_pdf.isEnabled() &&
        item.cargoCommodityGroupIsElectronicDg) ||
      (FlagsPermissionCargo.permission_cargo_print_sptj_pdf.isEnabled() &&
        item.cargoCommodityGroupIsDg &&
        !item.cargoCommodityGroupIsElectronicDg)
    ) {
      items.push(sptj);
    }

    items.push(manifest);

    return items;
  }

  actionGroupOptions(item: CargoV2Data): CargoV2ActionGroupOption[] {
    const onPrintSuccess = (row: CargoV2Data) => (flag: CargoV2PrintFlag) =>
      this.setPrintFlag(row, flag);

    const print = this.$refs.print as CargoV2PrintRef;

    const labelOption: CargoV2ActionGroupOption = {
      icon: "invoice",
      label: "Print Label kargo",
      clickFunction: (row) => {
        print?.printData?.(
          row.id,
          "label",
          "",
          row,
          onPrintSuccess(row)
        );
      }
    };

    const manifestOption: CargoV2ActionGroupOption = {
      icon: "printer",
      label: "Print Manifest kargo",
      clickFunction: (row) => {
        print?.printManifestV2?.(row.id, row, onPrintSuccess(row));
      }
    };

    const excelOption: CargoV2ActionGroupOption = {
      icon: "document-download-outline-grey",
      label: "Download Excel",
      clickFunction: (row) => {
        CargoV2Controller.getDownloadData({ id: row.id, type: "cargo" });
      }
    };

    const awbOption: CargoV2ActionGroupOption = {
      icon: "printer",
      label: "Print AWB Lion Cargo",
      clickFunction: (row) => {
        if (row.isNewForm) {
          return CargoV2Controller.downloadAwbReceipt(new DownloadAWBRequest({id: row.id, awbNumber: row.cargoNumber}));
        }
        NGenController.printAWBLionCargo({ AWBNo: row.cargoNumber });
      }
    };

    const eplOption: CargoV2ActionGroupOption = {
      icon: "download-gray",
      label: "Download EPL",
      clickFunction: (row) => {
        CargoV2Controller.getDownloadData({ id: row.id, type: "epl" });
      }
    };

    const ptiOption: CargoV2ActionGroupOption = {
      icon: "print-pti",
      label: "Print PTI",
      clickFunction: (row) => {
        print?.printPtiPdf?.(row.id, row, onPrintSuccess(row));
      }
    };

    const sptjElectronicOption: CargoV2ActionGroupOption = {
      icon: "print-sptj",
      label: "Print SPTJ",
      clickFunction: (row) => {
        print?.printSPTJElektronik?.(row.id, row, onPrintSuccess(row));
      }
    };

    const sptjNonElectronicOption: CargoV2ActionGroupOption = {
      icon: "print-sptj",
      label: "Print SPTJ",
      clickFunction: (row) => {
        print?.printSptjNonElectronic?.(
          row.id,
          row,
          onPrintSuccess(row)
        );
      }
    };

    const isPlane = item.cargoType === "plane";
    const isBTH =
      item.originCityCode === "BTH" || item.destinationCityCode === "BTH";

    if (!isPlane) {
      return [labelOption, manifestOption, excelOption];
    }

    if (isPlane && isBTH) {
      return [labelOption, awbOption, eplOption, manifestOption, excelOption];
    }

    const options: CargoV2ActionGroupOption[] = [labelOption, awbOption];

    if (FlagsPermissionCargo.permission_cargo_print_pti_pdf.isEnabled()) {
      options.push(ptiOption);
    }

    if (
      FlagsPermissionCargo.permission_cargo_print_electronic_sptj_pdf.isEnabled() &&
      item.cargoCommodityGroupIsElectronicDg
    ) {
      options.push(sptjElectronicOption);
    }

    if (
      FlagsPermissionCargo.permission_cargo_print_sptj_pdf.isEnabled() &&
      item.cargoCommodityGroupIsDg &&
      !item.cargoCommodityGroupIsElectronicDg
    ) {
      options.push(sptjNonElectronicOption);
    }

    options.push(manifestOption);
    options.push(excelOption);

    return options;
  }

  formatDate(dateStr: string, format = "DD MMMM YYYY"): string {
    if (!dateStr) return "";
    return this.$moment(dateStr)
      .locale("id")
      .format(format);
  }

  etdDayOffset(item: CargoV2Data): number {
    if (!item.estimationDepartureDate || !item.estimationArrivalDate) return 0;
    const dep = this.$moment(item.estimationDepartureDate).startOf("day");
    const arr = this.$moment(item.estimationArrivalDate).startOf("day");
    return Math.max(0, arr.diff(dep, "days"));
  }

  etdDayOffsetLabel(item: CargoV2Data): string {
    const days = this.etdDayOffset(item);
    if (days === 0) return "";
    return days === 1 ? "+1 hari" : `+${days} hari`;
  }

  fetchList(): void {
    IncomingOutgoingController.fetchCargoList();
  }

  onClickRow(item: CargoV2Data): void {
    const isNewForm = item.isNewForm;
    if (isNewForm) {
      router.push(
        `/incoming-outgoing/cargo-v2/${encodeURIComponent(
          item.cargoNumber
        )}/info?id=${item.id}`
      );
    } else {
      router.push(
        `/incoming-outgoing/cargo/${encodeURIComponent(item.cargoNumber)}?id=${item.id}`
      );
    }
  }

  openPrintPopover(event: Event, item: CargoV2Data): void {
    this.printPopoverItem = item;
    const op = this.$refs.printPopover as { toggle?: (e: Event) => void };
    op?.toggle?.(event);
  }

  closePrintPopover(): void {
    const op = this.$refs.printPopover as { hide?: () => void };
    op?.hide?.();
  }

  CalculateTimeDifference = CalculateTimeDifference;
  showRemainingTimePlane(
    time: string,
    cargoType: string,
    departureDate: string
  ) {
    return !(!time || cargoType !== "plane" || departureDate);
  }
  get DateNow() {
    return this.$moment().format("YYYY-MM-DDTHH:mm:ss");
  }
}

export default IncomingOutgoingCargoTable;
